/**
 * The emails a leave request sends as it moves.
 *
 * Their own file rather than more of emailNotification.ts, which already carries expenses,
 * nominations, polls, timesheets and regularizations. The shared chrome — the wrapper, the
 * button, the sender — is imported from there, so these look like every other mail the product
 * sends.
 *
 * ── HR is on every one of them ────────────────────────────────────────────────────────────
 *
 * Not by each function remembering to add an address, but because `leaveRecipients()` below is
 * the only way any of them builds a recipient list. HR was previously whoever happened to hold
 * the HR_Admin or Super_Admin role, which is a moving target: the day nobody holds it, or the
 * day the listUsers query fails, the leave record stops reaching HR entirely and nothing says
 * so. The fixed address is the floor under that, and the role lookup stays on top of it so a
 * newly appointed HR admin does not have to be added anywhere.
 *
 * ── Who each one goes to ──────────────────────────────────────────────────────────────────
 *
 *   submitted   the employee, their manager, HR
 *   approved    the employee, their manager, HR
 *   rejected    the employee, their manager, HR — always with the reason
 *   withdrawn   the manager and HR; the employee did it and already knows
 *   amended     the manager and HR, flagged when it voids an approval they had already given
 *
 * The employee is on the first three because a leave request is a decision about their time,
 * and "still waiting" and "rejected on Tuesday" look identical from a screen that says nothing.
 *
 * ── Why HR is a recipient and not a CC ────────────────────────────────────────────────────
 *
 * There is no CC to put them in. `SendEmailInput` is `{to, subject, htmlBody, textBody}` and the
 * email-sender Lambda builds `mailOptions` with no `cc` key, so a copied address would have to
 * be added to the GraphQL schema, the Lambda and a Terraform apply first — and AppSync rejects
 * an entire mutation that carries a field its deployed input type does not declare, so sending
 * `cc` before that apply landed would stop every email in the product, not just these. HR is
 * therefore on `to`, which delivers the same mail to the same people.
 */

import {
  APP_URL,
  actionButton,
  getHRAdminEmails,
  sendEmailSafe,
  wrapEmail,
} from './emailNotification';

/**
 * The address that receives every leave notification, whatever roles exist.
 *
 * Deliberately a constant and not a setting. The Email Settings screen has a `timesheetCcEmail`
 * for the equivalent job on timesheets, and it defaults to an address on a *different* domain
 * (creyentetech vs creyenteinfotech) — so reusing it would have quietly pointed leave mail
 * somewhere else. A second, differently-defaulted setting is how the two would drift; if this
 * ever needs to be configurable it should replace the constant, not sit beside it.
 */
export const HR_LEAVE_EMAIL = 'hr@creyenteinfotech.com';

/** Amber for awaiting-a-decision, matching the expense mails for the same state. */
const PENDING = '#f59e0b';
const APPROVED = '#0d8a6a';
const REJECTED = '#dc2626';
const NEUTRAL = '#64748b';

/**
 * Everyone who should receive one leave message.
 *
 * HR is always in the list — that is the whole point of routing every leave email through here.
 * Blank entries are dropped; `sendEmailSafe` lowercases and de-duplicates what survives, so an
 * HR admin who is also the manager receives one mail rather than two.
 */
async function leaveRecipients(...addresses: (string | undefined)[]): Promise<string[]> {
  const hrAdmins = await getHRAdminEmails();
  return [...addresses, ...hrAdmins, HR_LEAVE_EMAIL]
    .map(address => String(address || '').trim())
    .filter(Boolean);
}

/** What every one of these emails says about the request. Shared so they cannot disagree. */
export interface LeaveEmailFacts {
  employeeName: string;
  employeeEmail: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  /**
   * Working days, as the balance counts them — 0.5 for a half day.
   *
   * Passed in rather than derived here. This file has no business knowing about weekends or
   * the holiday calendar, and the number it used to compute for itself was calendar days with
   * a +1: an email said "4 days" for a Friday-to-Monday request while the balance deducted 2.
   * `leaveDaysFor` is the single source of truth and the caller already holds it.
   */
  days: number;
  /** 'FULL' | 'HALF_AM' | 'HALF_PM'. Only shown when it is not a full day. */
  session?: string;
  reason?: string;
  department?: string;
  /** The status the request is in as this email is sent. */
  status?: string;
  managerName?: string;
  managerEmail?: string;
}

const dateRangeOf = (facts: LeaveEmailFacts): string =>
  facts.startDate === facts.endDate ? facts.startDate : `${facts.startDate} to ${facts.endDate}`;

/** "Half day (morning)", or nothing at all for an ordinary full day. */
function sessionLabel(session?: string): string {
  const value = String(session || 'FULL').toUpperCase();
  if (value === 'HALF_AM') return 'Half day (first half)';
  if (value === 'HALF_PM') return 'Half day (second half)';
  return '';
}

/** "1 day" / "2.5 days" — the half survives, because a half-day request is the common case. */
const dayCount = (days: number): string => {
  const value = Number(days) || 0;
  const shown = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return `${shown} ${value === 1 ? 'day' : 'days'}`;
};

/**
 * The detail panel every leave email carries.
 *
 * One table, so an employee, their manager and HR are reading the same facts about the same
 * request rather than three summaries that were written separately and have since diverged.
 */
function leaveDetails(facts: LeaveEmailFacts, background: string, border: string, text: string): string {
  const half = sessionLabel(facts.session);
  const rows = [
    `<strong>Employee:</strong> ${facts.employeeName} (${facts.employeeEmail})`,
    facts.department ? `<strong>Department:</strong> ${facts.department}` : '',
    `<strong>Leave Type:</strong> ${facts.leaveType}`,
    `<strong>From:</strong> ${facts.startDate}`,
    `<strong>To:</strong> ${facts.endDate}`,
    `<strong>Duration:</strong> ${dayCount(facts.days)}`,
    half ? `<strong>Session:</strong> ${half}` : '',
    `<strong>Reason:</strong> ${facts.reason?.trim() || 'Not specified'}`,
    facts.status ? `<strong>Status:</strong> ${facts.status}` : '',
    facts.managerName || facts.managerEmail
      ? `<strong>Manager:</strong> ${facts.managerName || facts.managerEmail}`
      : `<strong>Manager:</strong> Not assigned`,
  ].filter(Boolean);

  return `
    <div style="background: ${background}; border: 1px solid ${border}; border-radius: 8px; padding: 16px; margin: 16px 0;">
      <p style="margin: 0; color: ${text}; font-size: 14px; line-height: 1.8;">
        ${rows.join('<br/>')}
      </p>
    </div>
  `;
}

const paragraph = (body: string): string =>
  `<p style="color: #1f2937; font-size: 15px; line-height: 1.6;">${body}</p>`;

/**
 * A leave request was filed.
 *
 * One message to the employee, their manager and HR together. It used to be two — a "please
 * review" to the manager and a near-identical "for your records" to HR — with the employee
 * told nothing at all, so the only person who could not confirm their own request had been
 * filed was the person who filed it.
 */
export async function emailLeaveApplied(facts: LeaveEmailFacts) {
  const recipients = await leaveRecipients(facts.employeeEmail, facts.managerEmail);
  if (recipients.length === 0) return;

  const html = wrapEmail(
    '📋 Leave Request Submitted',
    `
    ${paragraph(
      `<strong>${facts.employeeName}</strong> has submitted a leave request. It is awaiting ` +
        `${facts.managerName || facts.managerEmail ? 'their manager’s' : 'an'} approval.`
    )}
    ${leaveDetails({ ...facts, status: facts.status || 'Submitted' }, '#fef3c7', '#fde68a', '#92400e')}
    ${actionButton('View Leave Approvals', `${APP_URL}/leave/approvals`)}
  `,
    PENDING
  );

  await sendEmailSafe(recipients, `Leave Request Submitted - ${facts.employeeName}`, html);
}

/**
 * A manager decided.
 *
 * Employee, manager and HR in one message. A rejection always carries the reason: the approval
 * screen refuses to record one without it, so there is always something useful to say, and
 * "rejected" on its own is the least actionable message an employee can be sent.
 */
export async function emailLeaveDecision(
  facts: LeaveEmailFacts & { approved: boolean; decidedBy: string; decisionReason?: string }
) {
  const recipients = await leaveRecipients(facts.employeeEmail, facts.managerEmail);
  if (recipients.length === 0) return;

  const verb = facts.approved ? 'Approved' : 'Rejected';
  const accent = facts.approved ? APPROVED : REJECTED;
  const details = leaveDetails(
    { ...facts, status: verb },
    facts.approved ? '#f0fdf4' : '#fef2f2',
    facts.approved ? '#a7f3d0' : '#fecaca',
    facts.approved ? '#065f46' : '#991b1b'
  );

  const html = wrapEmail(
    `${facts.approved ? '✅' : '❌'} Leave Request ${verb}`,
    `
    ${paragraph(
      `<strong>${facts.employeeName}</strong>’s leave request has been ` +
        `<strong>${verb.toLowerCase()}</strong> by ${facts.decidedBy}.`
    )}
    ${details}
    ${
      facts.decisionReason?.trim()
        ? `<div style="background: #f8fafc; border-left: 4px solid ${accent}; border-radius: 6px; padding: 12px 16px; margin: 16px 0;">
             <p style="margin: 0; color: #334155; font-size: 14px; line-height: 1.6;">
               <strong>${verb === 'Rejected' ? 'Reason for rejection' : 'Note'}:</strong> ${facts.decisionReason.trim()}
             </p>
           </div>`
        : ''
    }
    ${paragraph(
      facts.approved
        ? 'This leave now appears on the shared team calendar.'
        : 'Please speak with your manager if you would like to discuss this further.'
    )}
    ${actionButton('View My Leave', `${APP_URL}/leave/dashboard`)}
  `,
    accent
  );

  await sendEmailSafe(recipients, `Leave Request ${verb} - ${facts.employeeName}`, html);
}

/**
 * An employee withdrew their own pending request.
 *
 * To the manager and HR, not the employee: they performed the action and already know. What
 * matters is clearing it out of an approval queue somebody is working through.
 */
export async function emailLeaveWithdrawn(facts: LeaveEmailFacts) {
  const recipients = await leaveRecipients(facts.managerEmail);
  if (recipients.length === 0) return;

  const html = wrapEmail(
    '↩️ Leave Request Withdrawn',
    `
    ${paragraph(
      `<strong>${facts.employeeName}</strong> (${facts.employeeEmail}) has withdrawn a pending ` +
        'leave request. No approval is needed.'
    )}
    ${leaveDetails({ ...facts, status: 'Withdrawn' }, '#f1f5f9', '#e2e8f0', '#334155')}
    ${actionButton('View Leave Approvals', `${APP_URL}/leave/approvals`)}
  `,
    NEUTRAL
  );

  await sendEmailSafe(
    recipients,
    `Leave Request Withdrawn - ${facts.employeeName}`,
    html
  );
}

/**
 * An employee changed a request that had already been filed.
 *
 * Flagged prominently when it had already been approved: the manager signed off on different
 * dates, and keeping that approval silently would book an absence nobody agreed to.
 */
export async function emailLeaveAmended(facts: LeaveEmailFacts & { wasApproved: boolean }) {
  const recipients = await leaveRecipients(facts.managerEmail);
  if (recipients.length === 0) return;

  const html = wrapEmail(
    facts.wasApproved ? '✏️ Approved Leave Changed — Re-approval Needed' : '✏️ Leave Request Updated',
    `
    ${paragraph(
      `<strong>${facts.employeeName}</strong> (${facts.employeeEmail}) has updated a leave request. ` +
        (facts.wasApproved
          ? 'It had already been approved, so it has returned to the pending queue and needs approving again.'
          : 'It is awaiting your approval.')
    )}
    ${leaveDetails(
      { ...facts, status: 'Submitted' },
      facts.wasApproved ? '#fef3c7' : '#eff6ff',
      facts.wasApproved ? '#fde68a' : '#bfdbfe',
      facts.wasApproved ? '#92400e' : '#1e40af'
    )}
    ${actionButton('Review Leave Approvals', `${APP_URL}/leave/approvals`)}
  `,
    facts.wasApproved ? '#b45309' : APPROVED
  );

  await sendEmailSafe(
    recipients,
    `${facts.wasApproved ? 'Leave Request Updated — Re-approval Needed' : 'Leave Request Updated'} - ${facts.employeeName}`,
    html
  );
}
